/*
 * File: GetPackage.ts
 * Author: Austin Robinson
 * Date Created: 8/4/2023
 * Copyright (c) DrivingDoc 2023
 */
import { SerializedEditorState } from 'lexical';
import { createQuery } from 'react-query-kit';

import { MinimalUser, PostGreSQLInterval, privateApi } from 'api';

export type GetPackageParams = {
  id: string;
};

export type GetPackageResult = {
  tenant_id: string;
  id: string;
  name: string;
  description: string | null;
  separate_charges: boolean;
  secure_seat_mode: 'at_time_of_enrollment' | 'when_deposit_paid' | 'when_full_cost_paid';
  advertise: boolean;
  deleted: boolean;
  welcome_letter: SerializedEditorState | null;
  agreement_id: string | null;
  agreement: {
    id: string;
    tenant_id: string;
    agreement_versions: {
      agreement_id: string;
      version: number;
      tenant_id: string;
      content: SerializedEditorState;
      effective_at: Date;
      created_at: Date;
      created_by?: string | null;
      created_by_user: MinimalUser | null;
    }[];
    created_at: Date;
    created_by?: string | null;
    created_by_user: MinimalUser | null;
  } | null;
  package_locations: {
    tenant_id: string;
    package_id: string;
    tenant_location_id: string;
    tenant_location: {
      tenant_id: string;
      id: string;
      name: string;
      street_address: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      disabled: boolean;
    };
  }[];
  package_courses: [
    {
      tenant_id: string;
      package_id: string;
      course_id: string;
      requisite_group: number;
      cost: number;
      deposit: number;
      deposit_due: string | null;
      deposit_due_from_enrollment: PostGreSQLInterval | null;
      remaining_due: string | null;
      remaining_due_from_enrollment: PostGreSQLInterval | null;
      require_prerequisite_verification: boolean;
      deleted: boolean;
      performance_time_included: PostGreSQLInterval;
      observation_time_included: PostGreSQLInterval;
      course: {
        tenant_id: string;
        id: string;
        course_type: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online';
        schedule_type: 'self_scheduled' | 'fixed' | 'manual' | 'no_schedule';
        title: string;
        description: string | null;
        created_at: string | null;
        created_by: string | null;
        updated_at: string | null;
        updated_by: string | null;
      };
    },
  ];
};

async function getPackage(params: GetPackageParams): Promise<GetPackageResult> {
  // make api call
  const result = await privateApi.get(`${import.meta.env.VITE_API_BASE_URL}/packages/${params.id}`, {});

  // return the data object
  return result.data;
}

const useGetPackage = createQuery<GetPackageResult, GetPackageParams, Error>({
  queryKey: ['package'],
  fetcher: (variables) => getPackage(variables),
});

export { getPackage, useGetPackage };
